import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowRight, Bell, Car } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

/*
    Header bell. Shows drivers' pending vehicle switch requests (the only thing it
    reports today): a count badge, a toast when a new one arrives while the
    dashboard is open, and a list that opens Vehicles → Switch Requests.

    Polls every 30 s: the requests are written by PublicRidesBackend, a different
    server, so there is no socket event for them here.
*/
const POLL_MS = 30000;

const timeAgo = ts => {
  const mins = Math.max(0, Math.round((Date.now() - Number(ts || 0)) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const seenIds = useRef(null);
  const boxRef = useRef(null);
  const allowed = !!user?.permissions?.vehicles;

  const { data } = useQuery(
    ['header-switch-requests'],
    async () => (await api.get('/vehicle-switch-requests', { params: { status: 'PENDING', limit: 8 } })).data,
    { enabled: allowed, refetchInterval: POLL_MS, refetchIntervalInBackground: true, refetchOnWindowFocus: true },
  );

  const items = data?.data || [];
  const count = data?.counts?.PENDING || 0;

  // Toast only for requests that arrived after the dashboard was opened.
  useEffect(() => {
    if (!data) return;
    const ids = items.map(r => r._id);
    if (seenIds.current) {
      const fresh = items.filter(r => !seenIds.current.has(r._id));
      fresh.forEach(r => {
        toast(`${r.driverName || 'A driver'} wants to switch to ${r.toRegNo}`, { icon: '🚗', duration: 6000 });
      });
    }
    seenIds.current = new Set([...(seenIds.current || []), ...ids]);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const openRequests = () => {
    setOpen(false);
    navigate('/vehicles/switch-requests');
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
        aria-label="Notifications">
        <Bell className="h-5 w-5" />
        {count > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-white">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open && (
        <div className="notifications-menu absolute right-0 mt-2 w-96 bg-white rounded-md shadow-lg py-1 z-50">
          <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
            {count > 0 ? <span className="text-xs text-gray-500">{count} pending</span> : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {!allowed || items.length === 0 ? (
              <div className="px-4 py-3">
                <p className="text-sm text-gray-600">No new notifications</p>
              </div>
            ) : items.map(r => (
              <button key={r._id} onClick={openRequests} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Car className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{r.driverName || 'A driver'}</span> requested a vehicle switch
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      {r.fromRegNo || '—'} <ArrowRight className="h-3 w-3" /> <span className="font-medium text-gray-700">{r.toRegNo}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{timeAgo(r.requestedAt)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {allowed && items.length > 0 ? (
            <button onClick={openRequests} className="w-full px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-gray-50 border-t border-gray-200">
              View all switch requests
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
